import React, { useState } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { DeleteOutline, WarningAmber } from '@mui/icons-material';
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';

const DeleteTripModal = ({ tripId, onDeleteSuccess }) => {
  const [show, setShow] = useState(false);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setShow(false);
    setReason('');
    setLoading(false);
  };

  const handleShow = () => setShow(true);

  const handleDelete = async () => {
    if (!reason.trim()) {
      toast.error('Please provide a reason for deletion');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.delete(`trips/${tripId}`, {
        data: { reason: reason.trim() }
      });

      toast.success(response.data.message || 'Trip deleted successfully');
      handleClose();
      
      // Callback to refresh the list
      if (onDeleteSuccess) {
        onDeleteSuccess();
      }
    } catch (error) {
      console.error('Error deleting trip:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete trip';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip title="Delete Trip">
        <IconButton
          size="small"
          color="error"
          onClick={handleShow}
          sx={{ ml: 1 }}
        >
          <DeleteOutline />
        </IconButton>
      </Tooltip>

      <Dialog
        open={show}
        onClose={handleClose}
        PaperProps={{
          style: {
            borderRadius: '12px',
            padding: '16px',
            maxWidth: '450px'
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center',
          pb: 1,
          "& .MuiTypography-root": {
            fontSize: '1.5rem',
            fontWeight: 600,
            color: '#dc2626'
          }
        }}>
          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
            <WarningAmber sx={{ color: '#dc2626', fontSize: 28 }} />
            <Typography>Delete Trip #{tripId}</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ pb: 3 }}>
          {/* Warning Box */}
          <Box sx={{ 
            mt: 1, 
            mb: 2, 
            p: 2, 
            backgroundColor: '#fef3c7', 
            borderRadius: '8px', 
            border: '1px solid #fbbf24' 
          }}>
            <Typography sx={{ fontSize: '0.85rem', color: '#92400e', fontWeight: '600' }}>
              ⚠️ Warning: Soft Delete
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#78350f', mt: 0.5 }}>
              This will hide the trip from normal views but can be restored from the audit trail. No data will be permanently deleted.
            </Typography>
          </Box>
          
          {/* Reason Input */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: '600', mb: 1, color: '#374151' }}>
              Reason for Deletion <span style={{ color: '#ef4444' }}>*</span>
            </Typography>
            <textarea
              placeholder="E.g., GPS data inconsistent, duplicate entry, manual correction needed..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              disabled={loading}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: reason.trim() ? '2px solid #10b981' : '2px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.95rem',
                outline: 'none',
                resize: 'vertical',
                fontFamily: 'inherit',
                opacity: loading ? 0.6 : 1
              }}
              onFocus={(e) => !loading && (e.target.style.borderColor = '#3b82f6')}
              onBlur={(e) => e.target.style.borderColor = reason.trim() ? '#10b981' : '#d1d5db'}
            />
            {!reason.trim() && (
              <Typography sx={{ fontSize: '0.75rem', color: '#ef4444', mt: 0.5 }}>
                This field is required and will be logged for compliance
              </Typography>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            variant="outlined"
            sx={{
              color: '#6b7280',
              borderColor: '#d1d5db',
              '&:hover': {
                borderColor: '#9ca3af',
                backgroundColor: '#f9fafb'
              },
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={loading || !reason.trim()}
            variant="contained"
            sx={{
              backgroundColor: '#dc2626',
              color: 'white',
              '&:hover': {
                backgroundColor: '#b91c1c'
              },
              '&:disabled': {
                backgroundColor: '#e5e7eb',
                color: '#9ca3af'
              },
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            {loading ? 'Deleting...' : 'Delete Trip'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DeleteTripModal;
